import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';

export interface Settore {
  id: string;
  organization_id: string;
  name: string;
  description?: string | null;
  color: string;
  sort_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string | null;
}

export interface SettoreFormData {
  name: string;
  description?: string | null;
  color: string;
  sort_order?: number;
  is_active?: boolean;
}

interface SettoriStore {
  // State
  settori: Settore[];
  loading: boolean;
  // true solo dopo un fetch andato a buon fine: distingue "lista vuota perché
  // l'org non ha settori" da "lista vuota perché non ancora caricata/fallita"
  fetched: boolean;
  error: string | null;

  // CRUD
  fetchSettori: () => Promise<void>;
  createSettore: (data: SettoreFormData) => Promise<Settore | null>;
  updateSettore: (id: string, data: Partial<SettoreFormData>) => Promise<Settore | null>;
  deleteSettore: (id: string) => Promise<boolean>;
  reorderSettori: (settoreIds: string[]) => Promise<boolean>;

  // Assignment
  assignSettoriToClient: (clientId: string, settoreIds: string[]) => Promise<boolean>;

  // Utility
  clearError: () => void;
}

export const useSettoriStore = create<SettoriStore>((set, get) => ({
  settori: [],
  loading: false,
  fetched: false,
  error: null,

  fetchSettori: async () => {
    set({ loading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('User not associated with an organization');

      const { data, error } = await supabase
        .from('settori')
        .select('*')
        .eq('organization_id', organizationId)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      set({ settori: data || [], fetched: true });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createSettore: async (settoreData: SettoreFormData) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Next sort order
      const { data: maxOrderData } = await supabase
        .from('settori')
        .select('sort_order')
        .eq('organization_id', userProfile.organization_id)
        .order('sort_order', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextSortOrder = (maxOrderData?.sort_order || 0) + 1;

      const { data, error } = await supabase
        .from('settori')
        .insert({
          organization_id: userProfile.organization_id,
          name: settoreData.name,
          description: settoreData.description || null,
          color: settoreData.color,
          sort_order: settoreData.sort_order || nextSortOrder,
          is_active: settoreData.is_active !== false,
          created_by: userData.user.id,
        })
        .select()
        .single();

      if (error) throw error;

      const settori = get().settori;
      set({ settori: [...settori, data].sort((a, b) => a.sort_order - b.sort_order) });

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  updateSettore: async (id: string, settoreData: Partial<SettoreFormData>) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('settori')
        .update(settoreData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const settori = get().settori.map(s => s.id === id ? data : s);
      set({ settori: settori.sort((a, b) => a.sort_order - b.sort_order) });

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  deleteSettore: async (id: string) => {
    set({ loading: true, error: null });
    try {
      // Junction rows are deleted via CASCADE on settore_id FK
      const { error } = await supabase
        .from('settori')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set({ settori: get().settori.filter(s => s.id !== id) });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  reorderSettori: async (settoreIds: string[]) => {
    set({ loading: true, error: null });
    try {
      const updates = settoreIds.map((settoreId, index) =>
        supabase
          .from('settori')
          .update({ sort_order: index + 1 })
          .eq('id', settoreId)
      );

      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) throw new Error(`Failed to reorder: ${errors[0].error?.message}`);

      await get().fetchSettori();
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  assignSettoriToClient: async (clientId: string, settoreIds: string[]) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Fetch existing assignments for diff (preserves created_at)
      const { data: existing } = await supabase
        .from('client_settori')
        .select('settore_id')
        .eq('client_id', clientId);

      const existingIds = (existing || []).map((a: { settore_id: string }) => a.settore_id);
      const toAdd = settoreIds.filter(id => !existingIds.includes(id));
      const toRemove = existingIds.filter((id: string) => !settoreIds.includes(id));

      if (toRemove.length > 0) {
        const { error } = await supabase
          .from('client_settori')
          .delete()
          .eq('client_id', clientId)
          .in('settore_id', toRemove);
        if (error) throw error;
      }

      if (toAdd.length > 0) {
        const rows = toAdd.map(settoreId => ({
          organization_id: userProfile.organization_id,
          client_id: clientId,
          settore_id: settoreId,
          created_by: userData.user!.id,
        }));
        const { error } = await supabase.from('client_settori').insert(rows);
        if (error) throw error;
      }

      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
